import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  ActivatedRouterStateSnapshot,
  BaseSiteService,
  CurrencyService,
  LanguageService,
  ProductSearchService,
  RoutingService
} from '@spartacus/core';
import { ProductListComponentService, ProductListRouteParams, SearchCriteria, ViewConfig } from '@spartacus/storefront';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { DpSearchService } from '@app/core/services/search.service';
import { PRODUCT_LIST_DEFAULT_QUERY } from '@app-modules/product/product-list/constants/product-list-default-query.constant';
import { DpMeasurementSystemService } from '@app-modules/site-context/facade/measurement-system.service';

@Injectable({ providedIn: 'root' })
export class DpProductListComponentService extends ProductListComponentService {
  readonly #siteContextService = inject(BaseSiteService);
  readonly #measurementSystemService = inject(DpMeasurementSystemService);
  readonly #searchService = inject(DpSearchService);

  constructor(
    productSearchService: ProductSearchService,
    routing: RoutingService,
    activatedRoute: ActivatedRoute,
    currencyService: CurrencyService,
    languageService: LanguageService,
    router: Router,
    config: ViewConfig
  ) {
    super(productSearchService, routing, activatedRoute, currencyService, languageService, router, config);
    this.searchByRouting$ = combineLatest([
      this.routing.getRouterState().pipe(distinctUntilChanged((x, y) => x.state.url === y.state.url)),
      ...this.dpSiteContext
    ]).pipe(
      map(([routerState]) => routerState.state),
      tap((state: ActivatedRouterStateSnapshot) => {
        const criteria = this.getCriteriaFromRoute(state.params, state.queryParams);
        this.search(criteria);
      })
    );
  }

  private get dpSiteContext(): Observable<string>[] {
    return [
      this.languageService.getActive(),
      this.#siteContextService.getActive(),
      this.currencyService.getActive(),
      this.#measurementSystemService.getActive()
    ];
  }

  getCriteriaQueryFromRoute(categoryCode: string, freeTextSearch?: string): string {
    const routeParams = this.activatedRoute.snapshot.params;
    const queryParams = this.activatedRoute.snapshot.queryParams;
    let queryString = `${PRODUCT_LIST_DEFAULT_QUERY}${categoryCode}`;
    if (queryParams?.query) {
      if (freeTextSearch) {
        queryString = ((queryParams as SearchCriteria)?.query ?? '')
          ?.replace(/(:[^:]+:)/, `$1allCategories:${categoryCode}:`)
          .replace(freeTextSearch, this.#searchService.escapeTerm(freeTextSearch));
      } else {
        queryString =
          (queryParams as SearchCriteria)?.query?.replace(/(:allCategories:)[^:]+/, `$1${categoryCode}`) ?? '';
      }
    } else if (freeTextSearch) {
      queryString = `${this.#searchService.escapeQuery(freeTextSearch)}${queryString}`;
    }

    return queryString || this.getQueryFromRouteParams({ ...routeParams, categoryCode }) || '';
  }

  protected getQueryFromRouteParams({ query, categoryCode, brandCode }: ProductListRouteParams): string | undefined {
    if (query) {
      return query;
    }
    if (categoryCode) {
      return PRODUCT_LIST_DEFAULT_QUERY + categoryCode;
    }
    if (brandCode) {
      return this.RELEVANCE_ALLCATEGORIES + brandCode;
    }
  }
}
